import { Box, HStack, IconButton, Image, VStack } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Button from "@pathwright/ui/src/components/button/Button"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import CardHeader from "@pathwright/ui/src/components/card/CardHeader"
import ErrorMessage from "@pathwright/ui/src/components/error/ErrorMessage"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Text from "@pathwright/ui/src/components/ui/Text"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import classNames from "classnames"
import get from "lodash/get"
import { useEffect, useState } from "react"
import styled from "styled-components"
import {
  MentorGroupSubscriptionFragment,
  OrderByDir,
  PositionedSchoolSubscriptionPlanFragmentDoc,
  PositioningContextTypeInput,
  SchoolSubscriptionDiscount,
  SchoolSubscriptionPlanFragment,
  useRedeemSchoolGiftSubscriptionMutation,
  useSchoolSubscriptionPlansQuery,
  useSubscriptionDiscountLazyQuery
} from "../api/generated"
import LazyAdaptedAuthContainer from "../auth/LazyAdaptedAuthContainer"
import ChakraTooltip from "../components/ChakraTooltip"
import { useCurrencyFormatter } from "../currency/CurrencyFormatterProvider"
import SimpleGroupSubscriptionMentorGroup from "../mentor-group/groups/SimpleGroupSubscriptionMentorGroup"
import Pathicon from "../pathicon/Pathicon"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import {
  PositioningContextProvider,
  sortPositionedObjects,
  usePositioningContext
} from "../positioning/PositioningContext"
import PWAdminHint from "../pw-admin/PWAdminHint"
import StripeProductCard from "../stripe/StripeProductCard"
import useStripeProducts from "../stripe/graphql/useStripeProducts"
import {
  getIsSubscriptionActive,
  getIsSubscriptionDelinquent
} from "../subscription/utils"
import { useUserCommunityContext } from "../user/UserCommunityContext"
import USER_SCHOOL_SUBSCRIPTION_QUERY from "../user/graphql/user-school-subscription-query"
import { getRelativeUrl, getSchoolUrl } from "../utils/urls"
import ConditionalOrderDiscountForm from "./ConditionalOrderDiscountForm"
import OrderStripeCheckoutForm from "./OrderStripeCheckoutForm"
import { getIntervalDisplayName } from "./utils/subscription"

const StyledCardBlock = styled(CardBlock)`
  .SubscriptionOptions > div {
    background-color: white;
  }
`

type OrderSubscriptionCardProps = {
  className?: string
  card?: any
  planIds?: string[]
  productIds?: string[]
  visibilityCode?: string
  visibilityCodeFilter?: {
    eq?: string
  }
  redirectTo: string
  inviteToken?: string
  userPropertyDefaults?: {
    [key: string]: any
  }
  reactivateMentorGroupId?: number
}

export default function OrderSubscriptionCard({
  className,
  card,
  planIds,
  productIds,
  visibilityCode,
  visibilityCodeFilter,
  redirectTo,
  inviteToken,
  userPropertyDefaults
}: OrderSubscriptionCardProps) {
  const { school } = usePathwrightContext()
  const { tc, tca } = useTranslate()
  const { activeCommunityGroupSubscription } = useUserCommunityContext()
  const userSubscriptionQuery = useQuery(USER_SCHOOL_SUBSCRIPTION_QUERY)
  const userSubscription = get(userSubscriptionQuery, "data.me.school_subscription.subscription") // prettier-ignore
  const hasActiveSubscription =
    getIsSubscriptionActive(userSubscription) ||
    !!activeCommunityGroupSubscription

  return (
    <NarrowCard
      className={classNames("OrderSubscriptionCard", className)}
      card={card}
      navless
      noaction
      emphasis="secondary"
    >
      <CardHeader
        card={card}
        title={
          hasActiveSubscription ? tca("Your Subscription") : tc("Subscribe")
        }
        meta={tc(`To {{ schoolName }}`, { schoolName: school.name })}
      />
      <OrderSubscriptionFlow
        planIds={planIds}
        productIds={productIds}
        visibilityCode={visibilityCode}
        visibilityCodeFilter={visibilityCodeFilter}
        redirectTo={redirectTo}
        inviteToken={inviteToken}
        userPropertyDefaults={userPropertyDefaults}
      />
    </NarrowCard>
  )
}

OrderSubscriptionCard.displayName = "OrderSubscriptionCard"

export function OrderSubscriptionFlow({
  planIds,
  productIds,
  visibilityCode,
  visibilityCodeFilter,
  redirectTo,
  inviteToken,
  userPropertyDefaults
}: OrderSubscriptionCardProps) {
  const { me, school } = usePathwrightContext()
  const {
    communityGroupMemberships,
    isCommunityGroupAdmin,
    activeCommunityGroupSubscription,
    communityGroupWithActiveSubscription,
    reactivateCommunityGroupWithInactiveSubscription,
    hasActiveMembership
  } = useUserCommunityContext()

  // To be safe, let's default the redirectTo to the current location if none provided.
  redirectTo ||= window.location.pathname + window.location.search

  const { t, tc, tca, Trans } = useTranslate()

  // Selection controls step of subscribe flow
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
    useState<SchoolSubscriptionPlanFragment | null>(null)
  const [authStep, setAuthStep] = useState(false)

  // Ignoring groups with a seat limit of 1 or less which are considered "individual" subscriptions
  // and do not benefit from reactivation, whereas reactivating a "group" subscription will automatically
  // retain the attached mentor group + memberships.
  function getReactivateMentorGroup(group?: MentorGroupSubscriptionFragment) {
    return (group?.seat_limit || 0) > 1 ? group : undefined
  }
  const [reactivateMentorGroup, setReactivateMentorGroup] = useState(
    getReactivateMentorGroup(reactivateCommunityGroupWithInactiveSubscription)
  )

  // The reactivateCommunityGroupWithInactiveSubscription may not be available on mount,
  // so let's set once it exists (if it does).
  useEffect(() => {
    if (reactivateCommunityGroupWithInactiveSubscription) {
      setReactivateMentorGroup(
        getReactivateMentorGroup(
          reactivateCommunityGroupWithInactiveSubscription
        )
      )
    }
  }, [reactivateCommunityGroupWithInactiveSubscription])

  const [stripeProducts, stripeProductsQuery] = useStripeProducts({
    ids: productIds
      ? {
          in: productIds
        }
      : null
  })

  const userSubscriptionQuery = useQuery(USER_SCHOOL_SUBSCRIPTION_QUERY)
  const userSubscription = get(userSubscriptionQuery, "data.me.school_subscription.subscription") // prettier-ignore

  const positioningContext = {
    contextType: PositioningContextTypeInput.SubscribeFlow,
    // TODO: what context key should be used?
    contextKey: "subscribe:flow"
  }
  const schoolSubscriptionPlansQuery = useSchoolSubscriptionPlansQuery({
    variables: {
      ids: planIds
        ? {
            in: planIds
          }
        : null,
      visibility_code: visibilityCode || null,
      visibility_code_filter: visibilityCodeFilter || null,
      positioningContext,
      orderBy: [{ position: OrderByDir.Asc }, { id: OrderByDir.Asc }]
    }
  })
  const flattenedSchoolSubscriptionPlans =
    schoolSubscriptionPlansQuery.data?.school?.subscription_plans?.edges?.map(
      (edge) => edge!.node!
    ) || []

  const schoolSubscriptionPlans = sortPositionedObjects(
    flattenedSchoolSubscriptionPlans
  )

  const error =
    userSubscriptionQuery.error ||
    stripeProductsQuery.error ||
    schoolSubscriptionPlansQuery.error
  const loading =
    userSubscriptionQuery.loading ||
    stripeProductsQuery.loading ||
    schoolSubscriptionPlansQuery.loading

  const planVisibilityCode =
    visibilityCode || (visibilityCodeFilter && visibilityCodeFilter.eq)

  if (error) {
    return (
      <BlankSlate
        icon="statuscaution"
        heading={tc("discussion.responses.unexpected_error")}
        body={<ErrorMessage error={error} />}
      />
    )
  }

  if (loading) {
    return (
      <VStack minH="300px">
        <LoadingCircle />
      </VStack>
    )
  }

  if (
    getIsSubscriptionActive(userSubscription) &&
    !userSubscription.trial_end_dtime
  ) {
    return getIsSubscriptionDelinquent(userSubscription) ? (
      <BlankSlate
        icon="recur"
        heading="Your subscription is expired."
        body="When attempting to renew your subscription the payment method failed. After multiple attempts, your subscription was cancelled. To restore your subscription access, please update your payment method:"
        primaryAction={{
          label: "Update Payment Method",
          to: "/manage/profile/billing/payment/"
        }}
      />
    ) : (
      <BlankSlate
        icon="recur"
        heading={`${tc(`You’ve already subscribed to {{ school }}`, {
          school: school.name
        })}!`}
        body={`${tc(`You may manage your subscription under your profile`)}.`}
      />
    )
  }

  if (communityGroupMemberships.length) {
    if (isCommunityGroupAdmin) {
      if (communityGroupWithActiveSubscription) {
        const group = communityGroupWithActiveSubscription
        if ((group.seat_limit || 0) > 1) {
          return (
            <StyledBlankSlate
              stack
              icon="recur"
              heading={
                <>
                  <Trans
                    i18nKey="Your group subscription to<br />{{ school }} is active"
                    tOptions={{
                      school: school.name
                    }}
                  />
                </>
              }
              primaryAction={{
                to: `/community/${group.id}/`,
                children: tc("Manage my group")
              }}
              secondaryAction={{
                to: "/library",
                children: t("View included t('path.label_plural')")
              }}
            />
          )
        } else {
          return (
            <StyledBlankSlate
              stack
              icon="recur"
              heading={tc(`Your subscription to {{ school }} is active`, {
                school: school.name
              })}
              primaryAction={{
                to: "/library",
                children: t("View included t('path.label_plural')")
              }}
            />
          )
        }
      }
    } else {
      if (communityGroupWithActiveSubscription) {
        const group = communityGroupWithActiveSubscription
        const subscription = activeCommunityGroupSubscription
        return (
          <StyledBlankSlate
            icon="recur"
            heading={tc(`You are subscribed through {{ group }}`, {
              group: group.name
            })}
            body={
              <>
                {tc(
                  `You have access to all courses included in {{ subscription }}`,
                  { subscription: subscription.price?.product?.name }
                )}
                .
                <br />
                <br />
                <Trans
                  i18nKey={
                    "If you'd like to change your subscription, please contact {{ owner }} at <a>{{ email }}</a>."
                  }
                  components={{
                    a: <a href={`mailto:${group.owner?.email}`} />
                  }}
                  tOptions={{
                    owner: group.owner?.full_name,
                    email: group.owner?.email
                  }}
                />
              </>
            }
          />
        )
      } else {
        // Only prevent subscribing if user has an active membership (not revoked).
        const activeMembership = communityGroupMemberships.find((membership) =>
          hasActiveMembership(membership.group)
        )
        if (activeMembership) {
          const group = activeMembership.group
          return (
            <StyledBlankSlate
              icon="recur"
              heading={tca(`Subscription Unavailable`)}
              body={
                <>
                  {tc(
                    `The {{ group }} subscription you are a part of isn't available`,
                    { group: group.name }
                  )}
                  .
                  <br />
                  <br />
                  <Trans
                    i18nKey="To resolve this issue, please contact the {{ group }} subscription owner, {{ owner }} at <a>{{ email }}</a>."
                    tOptions={{
                      group: group.name,
                      owner: group.owner?.full_name,
                      email: group.owner?.email
                    }}
                    components={{
                      a: <a href={`mailto:${group.owner?.email}`} />
                    }}
                  />
                </>
              }
            />
          )
        }
      }
    }
  }

  if (schoolSubscriptionPlans.length <= 0 && stripeProducts.length <= 0) {
    return (
      <BlankSlate icon="recur" heading={tc("No subscription plans found")} />
    )
  }

  if (selectedSubscriptionPlan) {
    if (!me) {
      return (
        <LazyAdaptedAuthContainer
          inviteToken={inviteToken}
          userPropertyDefaults={userPropertyDefaults}
        />
      )
    }
    return (
      <CardBlock>
        <Button
          brand
          styleType="text"
          onClick={() => setSelectedSubscriptionPlan(null)}
        >
          {tca("Change Plan")}
        </Button>
        <SubscriptionPlanOrderForm
          subscriptionPlan={selectedSubscriptionPlan}
          visibilityCode={visibilityCode}
          visibilityCodeFilter={visibilityCodeFilter}
          redirectTo={redirectTo}
        />
      </CardBlock>
    )
  }

  return (
    <StyledCardBlock>
      {!!reactivateMentorGroup && (
        <HStack bg="white" borderRadius="10px" py={2} px={3} spacing={4}>
          <IconButton
            aria-label="clear group"
            icon={<Pathicon icon="x" size={16} />}
            minH={6}
            minW={6}
            h={6}
            w={6}
            isRound
            onClick={() => setReactivateMentorGroup(undefined)}
            cursor="pointer"
          />
          <SimpleGroupSubscriptionMentorGroup
            mentorGroup={reactivateMentorGroup}
          />
        </HStack>
      )}
      <div className="SubscriptionOptions">
        {stripeProducts
          .filter((stripeProduct) => {
            // Filter out all stripe price + products that don't match the ones
            // set on the reactivateMentorGroup.
            // Backend prohibits reactivating a mentor group with a different product.
            // Only doing this when reactivating a mentor group subscription.
            return (
              !reactivateMentorGroup ||
              reactivateMentorGroup?.group_subscription?.price?.product?.id ===
                stripeProduct.id
            )
          })
          .map((stripeProduct) => (
            <StripeProductCard
              key={stripeProduct.id}
              stripeProduct={stripeProduct}
              onAuthStep={setAuthStep}
              redirectTo={redirectTo}
              inviteToken={inviteToken}
              reactivateMentorGroup={reactivateMentorGroup}
            />
          ))}
        {!authStep && !reactivateMentorGroup && (
          <PositioningContextProvider
            items={schoolSubscriptionPlans}
            itemKey="subscription:plan"
            itemFragmentDocument={PositionedSchoolSubscriptionPlanFragmentDoc}
            context={positioningContext}
          >
            <>
              {schoolSubscriptionPlans.map((plan, index) => (
                <PositionedSubscriptionPlanCard
                  key={plan.id}
                  plan={plan}
                  onSelectPlan={setSelectedSubscriptionPlan}
                  showPlanDetails
                  index={index}
                />
              ))}
            </>
          </PositioningContextProvider>
        )}
      </div>
    </StyledCardBlock>
  )
}

type SubscriptionPlanOrderFormProps = {
  subscriptionPlan: SchoolSubscriptionPlanFragment
  visibilityCode?: string
  visibilityCodeFilter?: {
    eq?: string
  }
  redirectTo: string
}

function SubscriptionPlanOrderForm({
  subscriptionPlan,
  visibilityCode,
  visibilityCodeFilter,
  redirectTo
}: SubscriptionPlanOrderFormProps) {
  const [loadDiscount] = useSubscriptionDiscountLazyQuery()
  const [discountCode, setDiscountCode] = useState("")
  const [discount, setDiscount] = useState<SchoolSubscriptionDiscount | null>(
    null
  )

  const { tc } = useTranslate()

  const userSubscriptionQuery = useQuery(USER_SCHOOL_SUBSCRIPTION_QUERY)
  // when a 100% discount coupon is applied, we must bypass the order API and use the redeem API
  const [redeemSchoolGiftSubscription, { error }] =
    useRedeemSchoolGiftSubscriptionMutation({
      variables: {
        school_subscription_plan_id: subscriptionPlan?.id!,
        code: discountCode
      },
      onCompleted: () => {
        // once subscription has been redeemed, route to confirmation
        // NOTE: redirectTo is always available as prop.
        try {
          // Get full URL.
          const schoolURL = new URL(getSchoolUrl(redirectTo))
          // Parse in the /thank-you/ route.
          const nextURL = new URL(
            getSchoolUrl(schoolURL.pathname, "/thank-you/", schoolURL.search)
          )
          // Navigate internally to the relative route
          window.App?.navigate(getRelativeUrl(nextURL.href))
          // Next, we reload the user's subscription, the expectation being
          // that this will trigger the next phase in whatever flow the SubscriptionPlanOrderForm
          // is a part of.
          // Reload legacy subscription store's user subscription.
          // @ts-expect-error
          window.App?.getStore("subscribe").resource.subscription.load(
            {},
            true /* forceReload */
          )
          // Reload user's subscription in Apollo cache.
          userSubscriptionQuery.refetch()
        } catch {
          // We'll just have to simply reload the current URL, but it's unlikely
          // we'll make it here.
          window.location.href = redirectTo
        }
      }
    })

  // NOTE: visibilityCodeFilter only supports eq right now.
  const planVisibilityCode =
    visibilityCode || (visibilityCodeFilter && visibilityCodeFilter.eq)

  return (
    <>
      <SubscriptionPlanCard plan={subscriptionPlan} />
      <br />
      <ConditionalOrderDiscountForm
        schoolSubscriptionPlanId={subscriptionPlan.id!}
        applyDiscountCode={async (code) => {
          const discountQuery = await loadDiscount({
            variables: {
              discount_code: code,
              school_plan_id: subscriptionPlan.id!
            },
            fetchPolicy: "network-only"
          })
          const discount = get(
            discountQuery,
            "data.schoolSubscriptionDiscount",
            null
          )
          setDiscount(discount)
          setDiscountCode(discount ? code : "")
          // TODO: need to fix typing here.
          return discount! as any
        }}
        clearDiscountCode={() => {
          setDiscount(null)
          setDiscountCode("")
        }}
      />
      <br />
      {/* 100% off coupon where subscription plan does not require card info must be handled via redemption API */}
      {discount &&
      (discount.discount_percent || 0) >= 1 &&
      subscriptionPlan?.subscription_plan?.trial_type !== 10 ? (
        <CenteredContent>
          <SubmitButton
            styleType="primary"
            size="large"
            onClick={() => redeemSchoolGiftSubscription()}
          >
            {tc("Redeem")}
          </SubmitButton>
        </CenteredContent>
      ) : (
        <OrderStripeCheckoutForm
          disabled={false}
          stripeApiKey={null}
          renderButton={null}
          orderType={
            subscriptionPlan?.subscription_plan?.interval === 20
              ? "createLifetimeSchoolSubscriptionOrder"
              : "createSchoolSubscriptionOrder"
          }
          orderData={{
            plan_id: subscriptionPlan.id,
            plan_visibility_code: planVisibilityCode,
            coupon_code: discount && discountCode ? discountCode : null,
            redirect_to: redirectTo
          }}
        />
      )}
      {!!error && <Alert error={error} />}
    </>
  )
}

type SubscriptionPlanCardProps = {
  plan: SchoolSubscriptionPlanFragment
  showPlanDetails?: boolean
  onSelectPlan?: (plan: SchoolSubscriptionPlanFragment) => void
}

function SubscriptionPlanCard({
  plan,
  showPlanDetails,
  onSelectPlan
}: SubscriptionPlanCardProps) {
  const { t, tca } = useTranslate()
  const { currencyFormatter } = useCurrencyFormatter()

  return (
    <PWAdminHint model={plan.subscription_plan!}>
      <PlanCard key={plan.id}>
        <PlanHeader>
          <Image
            src={plan.image!}
            alt={plan.subscription_plan!.name || "Subscription Plan Image"}
          />
          <div>
            <Text.H4>{plan.subscription_plan!.name}</Text.H4>
          </div>
        </PlanHeader>
        {plan.details_html && showPlanDetails ? (
          <StyledHTMLBody
            dangerouslySetInnerHTML={{ __html: plan.details_html }}
          />
        ) : (
          <br />
        )}
        <Text.Body>
          <b style={{ color: "rgb(42, 46, 51)" }}>
            {currencyFormatter.format(plan.subscription_plan!.amount)}
          </b>{" "}
          {t("per")}{" "}
          {getIntervalDisplayName(plan.subscription_plan!.interval, t)}
        </Text.Body>
        {onSelectPlan ? (
          <>
            <br />
            <Button
              styleType="primary"
              onClick={() => onSelectPlan(plan)}
              brand
            >
              {tca("Subscribe")}
            </Button>
          </>
        ) : null}
      </PlanCard>
    </PWAdminHint>
  )
}

type PositionedSubscriptionPlanCardProps = {
  plan: SchoolSubscriptionPlanFragment
  showPlanDetails?: boolean
  onSelectPlan?: (plan: SchoolSubscriptionPlanFragment) => void
  index: number
}

function PositionedSubscriptionPlanCard({
  plan,
  showPlanDetails,
  onSelectPlan,
  index
}: PositionedSubscriptionPlanCardProps) {
  const { me } = usePathwrightContext()
  const { updatePositionedObject } = usePositioningContext()

  // Only allow superusers initially.
  const canPosition = !!me?.permissions?.has_pathwright_staff_access && !!index

  function handleMoveUp() {
    updatePositionedObject({
      item: plan,
      index: index - 1
    })
  }

  return (
    <Box pos="relative">
      <SubscriptionPlanCard
        plan={plan}
        showPlanDetails={showPlanDetails}
        onSelectPlan={onSelectPlan}
      />
      {/* Only allow superusers initially. */}
      {canPosition && (
        <ChakraTooltip
          fitContent
          title="Move up (only visible to super users)."
        >
          <IconButton
            className="SchoolSubscriptionPlanReorder"
            aria-label="reorder"
            icon={<Pathicon icon="chevron-up" />}
            cursor="pointer"
            isRound={true}
            h={8}
            minW={8}
            onClick={handleMoveUp}
            pos="absolute"
            top={0}
            right={0}
            mt={2}
            mr={2}
          />
        </ChakraTooltip>
      )}
    </Box>
  )
}

const StyledHTMLBody = styled(Text.Body)`
  ul {
    list-style: unset;
    padding-left: 40px;
  }
`

const NarrowCard = styled(Card)`
  & > .View {
    max-width: 500px;
  }
  margin-bottom: 100px;
`

const PlanCard = styled.div`
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 20px;
  position: relative;

  &:first-child {
    margin-top: 0;
    margin-bottom: 20px;

    .SchoolSubscriptionPlanReorder {
      display: none;
    }
  }

  &:not(:first-child) {
    margin: 10px 0 20px;
  }
`

const PlanHeader = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-column-gap: 0.5em;
  align-items: center;
  img {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
  }
`

const CenteredContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
`

const StyledBlankSlate = styled(BlankSlate)`
  padding: 30px 40px 40px !important;
`
